/* global globalThis */
/**
 * Region / country routing utilities.
 *
 * Kept in a dependency-free module (only browser globals + env vars) so the
 * domain-routing logic can be unit-tested in isolation. Re-exported from
 * `utils/index.js`, so existing `from 'utils'` imports are unaffected.
 */

// Supported ISO country codes used for region selection.
const COUNTRY = { US: 'US', GB: 'GB' };

// Root domain shared by every app hostname.
const SUBSCRIBILI_DOMAIN_SUFFIX = '.subscribili.com';

// Region subdomain segment per country (GB -> app.uk.*, US -> app.us.*).
const REGION_SUBDOMAIN = { [COUNTRY.GB]: 'uk', [COUNTRY.US]: 'us' };
const REGION_SEGMENTS = Object.values(REGION_SUBDOMAIN);

// Leading hostname labels that are served directly on the bare host and must be
// exempt from geolocation region-routing (e.g. pay.<env>.subscribili.com stays
// put instead of redirecting to pay.us.*). Matched against hostname.split('.')[0].
const REGION_EXEMPT_SUBDOMAINS = new Set(['pay']);

// Storage keys.
const SELECTED_COUNTRY_KEY = 'selectedCountry';
const COUNTRY_MANUAL_KEY = 'countryManuallySelected';

/**
 * Get selected country from sessionStorage, localStorage, or default to US
 * @returns {string} - Country code (e.g. 'US', 'GB')
 */
export const getSelectedCountry = () =>
  sessionStorage.getItem(SELECTED_COUNTRY_KEY) ||
  localStorage.getItem(SELECTED_COUNTRY_KEY) ||
  COUNTRY.US;

/**
 * Persist selected country to sessionStorage and localStorage
 * @param {string} countryCode - Country code (e.g. 'US', 'GB')
 */
export const setSelectedCountry = (countryCode) => {
  sessionStorage.setItem(SELECTED_COUNTRY_KEY, countryCode);
  localStorage.setItem(SELECTED_COUNTRY_KEY, countryCode);
};

// Single source of truth: maps ?redirect= param value -> country code.
// The inverse (country code -> param value) is derived below.
export const REDIRECT_PARAM_TO_COUNTRY = { us: COUNTRY.US, uk: COUNTRY.GB };
export const COUNTRY_TO_REDIRECT_PARAM = Object.fromEntries(
  Object.entries(REDIRECT_PARAM_TO_COUNTRY).map(([param, code]) => [code, param])
);

/**
 * Mark that the user has explicitly chosen a country from the dropdown.
 * Used for same-domain refreshes after a manual selection with no URL redirect.
 */
export const setCountryManuallySelected = () => {
  sessionStorage.setItem(COUNTRY_MANUAL_KEY, '1');
};

/** Returns true if the user has manually selected a country this session. */
export const isCountryManuallySelected = () =>
  sessionStorage.getItem(COUNTRY_MANUAL_KEY) === '1';

/**
 * Clear the manual-selection flag. Call this on logout so the next fresh
 * login session gets the IP-based default again.
 */
export const clearCountryManuallySelected = () => {
  sessionStorage.removeItem(COUNTRY_MANUAL_KEY);
};

/**
 * Reads the ?redirect= query param from the current URL, seeds sessionStorage
 * with the manual-selection flag, and strips the param from the URL so it
 * doesn't linger in the address bar.
 * Returns the resolved country code (e.g. 'US', 'GB') or null if not present.
 */
export const consumeRedirectParam = () => {
  const params = new URLSearchParams(globalThis.location.search);
  const redirectValue = params.get('redirect');
  const countryCode = REDIRECT_PARAM_TO_COUNTRY[redirectValue?.toLowerCase()];
  if (!countryCode) return null;
  setCountryManuallySelected();
  params.delete('redirect');
  const newSearch = params.toString();
  const query = newSearch ? `?${newSearch}` : '';
  const cleanUrl = `${globalThis.location.pathname}${query}`;
  globalThis.history.replaceState(null, '', cleanUrl);
  return countryCode;
};

/** Supported countries for login/header dropdown (single source of truth) */
export const SUPPORTED_COUNTRIES = [
  { code: COUNTRY.US, name: 'United States' },
  { code: COUNTRY.GB, name: 'United Kingdom' },
];

/**
 * Whether the 3-domain multi-region model is enabled for this build.
 * When ON: app.subscribili.com is a geolocation-only landing (no region),
 * US lives at app.us.subscribili.com and UK at app.uk.subscribili.com.
 * When OFF (default): legacy 2-domain model — app.subscribili.com is US.
 */
export const isMultiRegionEnabled = () =>
  process.env.REACT_APP_MULTI_REGION_ENABLED === 'true';

/** True when the current hostname is under *.subscribili.com. */
export const isSubscribiliHost = () =>
  globalThis.location.hostname.endsWith(SUBSCRIBILI_DOMAIN_SUFFIX);

/**
 * True when the current hostname's leading label is region-exempt (e.g. pay.*).
 * These hosts serve directly and never region-redirect, mirroring how
 * non-subscribili.com hosts (vynedentalplans) are left alone.
 */
export const isRegionExemptHost = () =>
  REGION_EXEMPT_SUBDOMAINS.has(globalThis.location.hostname.split('.')[0]);

/**
 * Returns the region subdomain segment of the current hostname ('uk', 'us'),
 * or null if there isn't one (bare app.subscribili.com or non-subscribili host).
 */
const getHostnameRegionSegment = () => {
  if (!isSubscribiliHost()) return null;
  const segment = globalThis.location.hostname.split('.')[1];
  return REGION_SEGMENTS.includes(segment) ? segment : null;
};

/**
 * True on the bare geolocation-landing domain (app.subscribili.com) when
 * multi-region is enabled — a subscribili host with no region segment. Used to
 * route every visit (including authenticated) off to a region domain so the
 * landing never serves app content.
 */
export const isGeolocationLandingDomain = () =>
  isMultiRegionEnabled() &&
  isSubscribiliHost() &&
  !isRegionExemptHost() &&
  getHostnameRegionSegment() === null;

/**
 * True when the current hostname is a dedicated region subdomain (app.uk.* /
 * app.us.*) whose region is fixed by the URL, so the geolocation API call
 * should be skipped. Only applies in the multi-region model; in the legacy
 * 2-domain model this is always false so the existing login flow is preserved.
 */
export const isRegionSubdomain = () => {
  if (!isMultiRegionEnabled()) return false;
  return getHostnameRegionSegment() !== null;
};

/**
 * Returns the country code implied by the current hostname, or null when the
 * region is unresolved (non-subscribili.com host, or the geolocation landing).
 *   app.uk.<env>.subscribili.com → 'GB' (always)
 * Multi-region ON:
 *   app.us.<env>.subscribili.com → 'US'
 *   app.<env>.subscribili.com    → null (geolocation landing)
 * Multi-region OFF (legacy):
 *   app.<env>.subscribili.com    → 'US'
 */
export const getRegionFromHostname = () => {
  if (!isSubscribiliHost()) return null;
  const segment = getHostnameRegionSegment();
  // app.uk.* is GB in both models; app.us.* is US only in the multi-region model.
  if (segment === REGION_SUBDOMAIN[COUNTRY.GB]) return COUNTRY.GB;
  if (segment === REGION_SUBDOMAIN[COUNTRY.US] && isMultiRegionEnabled()) return COUNTRY.US;
  // Bare app.subscribili.com: geolocation landing when multi-region is on, US otherwise.
  return isMultiRegionEnabled() ? null : COUNTRY.US;
};

/**
 * Returns the origin for the given country code on the current environment,
 * derived by replacing the region subdomain segment.
 * Returns null on non-subscribili.com hostnames and region-exempt hosts (pay.*)
 * so no redirect occurs there.
 * Multi-region ON (US has its own subdomain):
 *   'GB' on app.qa.subscribili.com    → https://app.uk.qa.subscribili.com
 *   'US' on app.qa.subscribili.com    → https://app.us.qa.subscribili.com
 *   'US' on app.uk.qa.subscribili.com → https://app.us.qa.subscribili.com
 * Multi-region OFF (legacy, US is the bare domain):
 *   'GB' on app.qa.subscribili.com    → https://app.uk.qa.subscribili.com
 *   'US' on app.uk.qa.subscribili.com → https://app.qa.subscribili.com
 */
export const getRegionalOrigin = (countryCode) => {
  if (!isSubscribiliHost() || isRegionExemptHost()) return null;
  const { hostname, protocol } = globalThis.location;
  const parts = hostname.split('.');
  // Strip an existing region segment ('uk'/'us') so we start from the bare host.
  const base = getHostnameRegionSegment() ? [parts[0], ...parts.slice(2)] : parts;
  let newParts;
  if (countryCode === COUNTRY.GB) {
    newParts = [base[0], REGION_SUBDOMAIN[COUNTRY.GB], ...base.slice(1)];
  } else if (isMultiRegionEnabled()) {
    newParts = [base[0], REGION_SUBDOMAIN[COUNTRY.US], ...base.slice(1)];
  } else {
    newParts = base;
  }
  return `${protocol}//${newParts.join('.')}`;
};

/**
 * Display label for country code (e.g. GB -> 'UK')
 * @param {string} code - Country code (e.g. 'US', 'GB')
 * @returns {string} - Display label (e.g. 'US', 'UK')
 */
export const getCountryDisplayLabel = (code) => {
  if (!code) return code;
  return String(code).toUpperCase() === COUNTRY.GB ? 'UK' : code;
};

/**
 * Get API base URL based on selected country
 * @param {string} countryCode - Country code (US, GB, etc.)
 * @returns {string} - API base URL
 */
export const getApiBaseUrl = (countryCode = null) => {
  const selectedCountry = countryCode || getSelectedCountry();
  const defaultBaseUrl = process.env.REACT_APP_API_BASE_URL_V2 || 'https://api.subscribili.com';

  // Get country-specific environment variables
  const usBaseUrl = process.env.REACT_APP_API_BASE_URL_V2_US;
  const ukBaseUrl = process.env.REACT_APP_API_BASE_URL_V2_UK;

  // Map country codes to base URLs
  let baseUrl;
  if (selectedCountry === COUNTRY.US && usBaseUrl) {
    baseUrl = usBaseUrl;
  } else if (selectedCountry === COUNTRY.GB && ukBaseUrl) {
    baseUrl = ukBaseUrl;
  } else {
    baseUrl = defaultBaseUrl;
  }

  return baseUrl;
};
